import React, { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import Headers from '../components/Headers'
import Footer from '../components/Footer'
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { customer_logout, user_reset } from '../store/reducers/authReducer'
import { reset_count } from '../store/reducers/cardReducer'
import CustomerProfileSummary from '../components/customer/CustomerProfileSummary'
import { RxDashboard } from 'react-icons/rx'
import { FaUserCircle, FaBell, FaBoxOpen, FaHeart, FaStore, FaGift, FaWallet, FaCoins, FaShieldAlt, FaKey, FaHistory, FaComments, FaHeadset, FaChevronDown } from 'react-icons/fa'
import { BiLogInCircle } from 'react-icons/bi'
import { disconnectSocket } from '../utilies/config'

// ✅ Yalnız MÖVCUD route-lar (broken link yoxdur)
// `groupKey`/`titleKey` → i18n; visible text resolved in render.
const NAV_GROUPS = [
    {
        group: 'Hesab', groupKey: 'dashboard.groups.account',
        items: [
            { title: 'Hesab xülasəsi', titleKey: 'dashboard.items.summary',       icon: <RxDashboard />,   path: '/dashboard',               exact: true },
            { title: 'Profilim',       titleKey: 'dashboard.items.profile',       icon: <FaUserCircle />,   path: '/dashboard/profile' },
            { title: 'Bildirişlər',    titleKey: 'dashboard.items.notifications', icon: <FaBell />,         path: '/dashboard/notifications' },
        ],
    },
    {
        group: 'Sifarişlər və alışlar', groupKey: 'dashboard.groups.orders',
        items: [
            { title: 'Sifarişlərim',          titleKey: 'dashboard.items.myOrders',  icon: <FaBoxOpen />, path: '/dashboard/my-orders' },
            { title: 'İstək siyahım',         titleKey: 'dashboard.items.wishlist',  icon: <FaHeart />,   path: '/dashboard/my-wishlist' },
            { title: 'İzlədiyim mağazalar',   titleKey: 'dashboard.items.following', icon: <FaStore />,   path: '/dashboard/following' },
            { title: 'Çəkilişlərim',          titleKey: 'dashboard.items.giveaways', icon: <FaGift />,    path: '/dashboard/giveaways' },
            { title: 'Dəstək müraciətlərim',  titleKey: 'dashboard.items.myTickets', icon: <FaHeadset />, path: '/support/tickets' },
        ],
    },
    {
        group: 'Balans və ödəniş', groupKey: 'dashboard.groups.balance',
        items: [
            { title: 'Balansım',  titleKey: 'dashboard.items.balance',  icon: <FaWallet />, path: '/dashboard/my-balance' },
            { title: 'Cashback',  titleKey: 'dashboard.items.cashback', icon: <FaCoins />,  path: '/dashboard/cashback' },
        ],
    },
    {
        group: 'Təhlükəsizlik', groupKey: 'dashboard.groups.security',
        items: [
            { title: 'Hesab təhlükəsizliyi', titleKey: 'dashboard.items.security',        icon: <FaShieldAlt />, path: '/dashboard/security' },
            { title: 'Şifrə dəyişdir',       titleKey: 'dashboard.items.changePassword',  icon: <FaKey />,       path: '/dashboard/chage-password' },
            { title: 'Giriş qeydləri',       titleKey: 'dashboard.items.accessLogs',      icon: <FaHistory />,   path: '/dashboard/access-logs' },
        ],
    },
    {
        group: 'Digər', groupKey: 'dashboard.groups.other',
        items: [
            { title: 'Çat',            titleKey: 'dashboard.items.chat',          icon: <FaComments />, path: '/dashboard/chat' },
            { title: 'Dəstək mərkəzi', titleKey: 'dashboard.items.supportCenter', icon: <FaHeadset />,  path: '/support' },
        ],
    },
]

const Dashboard = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { t } = useTranslation()
    const { pathname } = useLocation()
    const [drawerOpen, setDrawerOpen] = useState(false)
    const [collapsed, setCollapsed] = useState({})

    const logout = async () => {
        disconnectSocket()
        dispatch(user_reset())
        dispatch(reset_count())
        await dispatch(customer_logout())
        navigate('/login', { replace: true })
    }

    const isActive = (item) => item.exact ? pathname === item.path : (pathname === item.path || pathname.startsWith(item.path + '/'))
    const activeGroup = useMemo(() => NAV_GROUPS.find(g => g.items.some(isActive))?.group, [pathname])
    const toggleGroup = (g) => setCollapsed(prev => ({ ...prev, [g]: !prev[g] }))
    const closeDrawer = () => setDrawerOpen(false)

    const SidebarInner = () => (
        <nav className='py-2'>
            {NAV_GROUPS.map(({ group, groupKey, items }) => {
                const open = !collapsed[group] || activeGroup === group
                return (
                    <div key={group} className='mb-1'>
                        <button
                            type='button'
                            onClick={() => toggleGroup(group)}
                            className='w-full flex items-center justify-between px-4 pt-3 pb-1 text-[11px] font-bold uppercase tracking-wider text-slate-400 hover:text-slate-600'
                        >
                            <span>{t(groupKey, { defaultValue: group })}</span>
                            <FaChevronDown size={9} className={`transition-transform ${open ? '' : '-rotate-90'}`} />
                        </button>
                        {open && (
                            <ul className='px-2'>
                                {items.map((item) => {
                                    const active = isActive(item)
                                    return (
                                        <li key={item.path + item.title}>
                                            <Link
                                                to={item.path}
                                                onClick={closeDrawer}
                                                className={`relative flex items-center gap-3 rounded-card px-3 py-2 text-sm mb-0.5 transition-all ${
                                                    active
                                                        ? 'bg-brand-600 text-white font-semibold shadow-sm'
                                                        : 'text-slate-600 hover:bg-slate-100 hover:pl-4'
                                                }`}
                                            >
                                                {active && <span className='absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-full bg-white/80' />}
                                                <span className='text-base shrink-0'>{item.icon}</span>
                                                <span className='truncate'>{t(item.titleKey, { defaultValue: item.title })}</span>
                                            </Link>
                                        </li>
                                    )
                                })}
                            </ul>
                        )}
                    </div>
                )
            })}

            <div className='mt-2 pt-2 border-t border-slate-100 px-2'>
                <button
                    onClick={logout}
                    className='w-full flex items-center gap-3 rounded-card px-3 py-2 text-sm text-rose-600 hover:bg-rose-50 transition-colors'
                >
                    <span className='text-base'><BiLogInCircle /></span>
                    <span>{t('dashboard.logout')}</span>
                </button>
            </div>
        </nav>
    )

    return (
        <div className='bg-slate-50 min-h-screen'>
            <Headers />

            {/* NOTE: Tailwind config desktop-first (max-width) — lg=≤1080, md=≤768 */}
            <div className='w-full max-w-marketplace mx-auto px-8 lg:px-4 py-6 md:pb-28'>
                {/* Profil xülasəsi (üstdə) */}
                <CustomerProfileSummary />

                {/* Mobil/tablet menyu düyməsi (yalnız ≤1080) */}
                <button
                    onClick={() => setDrawerOpen(true)}
                    className='hidden lg:inline-flex mt-4 items-center gap-2 rounded-card bg-white border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm w-full justify-center'
                >
                    <RxDashboard /> {t('dashboard.menu')}
                </button>

                <div className='mt-4 flex gap-6 items-start'>
                    {/* Desktop sidebar — yalnız >1080 */}
                    <aside className='block lg:hidden w-[270px] shrink-0 rounded-2xl bg-white border border-slate-200 shadow-card sticky top-24'>
                        <SidebarInner />
                    </aside>

                    {/* Content — həmişə tam en */}
                    <main className='flex-1 min-w-0 w-full'>
                        <Outlet />
                    </main>
                </div>
            </div>

            {/* Mobil drawer (state ilə; menyu düyməsi yalnız mobil/tabletdə görünür) */}
            {drawerOpen && (
                <div className='fixed inset-0 z-[60]'>
                    <div className='absolute inset-0 bg-black/40' onClick={closeDrawer} />
                    <div className='absolute left-0 top-0 h-full w-[82%] max-w-[300px] bg-white shadow-xl overflow-y-auto'>
                        <div className='flex items-center justify-between px-4 py-3 border-b border-slate-100 sticky top-0 bg-white'>
                            <span className='font-bold text-slate-800'>{t('dashboard.myAccount')}</span>
                            <button onClick={closeDrawer} className='text-slate-400 text-2xl leading-none'>×</button>
                        </div>
                        <SidebarInner />
                    </div>
                </div>
            )}

            <Footer />
        </div>
    )
}

export default Dashboard
